const STORAGE_PREFIX = 'woof-woof:notifications:seen:';
const BOOTSTRAP_PREFIX = 'woof-woof:notifications:bootstrapped:';
const MAX_KEYS = 250;
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 21;

type DedupRecord = Record<string, string>;

function getStorageKey(profileId: string): string {
  return `${STORAGE_PREFIX}${profileId}`;
}

function getBootstrapKey(profileId: string, namespace: string): string {
  return `${BOOTSTRAP_PREFIX}${profileId}:${namespace}`;
}

function readRecord(profileId: string): DedupRecord {
  if (typeof window === 'undefined') {
    return {};
  }

  const raw = window.localStorage.getItem(getStorageKey(profileId));

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as DedupRecord;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function pruneRecord(record: DedupRecord): DedupRecord {
  const entries = Object.entries(record)
    .filter(([, createdAt]) => {
      const timestamp = new Date(createdAt).getTime();
      return Number.isFinite(timestamp) && Date.now() - timestamp <= MAX_AGE_MS;
    })
    .sort((left, right) => new Date(right[1]).getTime() - new Date(left[1]).getTime())
    .slice(0, MAX_KEYS);

  return Object.fromEntries(entries);
}

function writeRecord(profileId: string, record: DedupRecord): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getStorageKey(profileId), JSON.stringify(pruneRecord(record)));
}

export function hasSeenNotification(profileId: string, dedupKey: string): boolean {
  const record = readRecord(profileId);
  return Boolean(record[dedupKey]);
}

export function markNotificationSeen(profileId: string, dedupKey: string): void {
  const record = readRecord(profileId);
  record[dedupKey] = new Date().toISOString();
  writeRecord(profileId, record);
}

export function shouldBootstrapNotifications(profileId: string, namespace: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(getBootstrapKey(profileId, namespace)) !== 'true';
}

export function markNotificationsBootstrapped(profileId: string, namespace: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getBootstrapKey(profileId, namespace), 'true');
}
